import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useDriverDataContext } from '../../driverData/context/DriverDataContext'
import { extractVariableNames, findDriverForVariable, getDriverValueForPeriod } from '../utils/formulaCalculation'
import { EnhancedStatementRow } from '../utils/dataEnhancement'
import { StatementColumn, StatementDataType } from '../types'
import { forecastCursorSelector } from '../../../redux/context/filters/selectors'

interface UseFormulaCalculationParams {
  data: EnhancedStatementRow | undefined
  statementColumn: StatementColumn | undefined
}

export type FormulaStatus = 'complete' | 'partial' | 'none' | 'fiscalYear' | null

interface UseFormulaCalculationReturn {
  hasFormulaCalculation: boolean // Backward compatibility - true if status is 'complete'
  formulaStatus: FormulaStatus
  periodInfo: { year: number; month: number } | null
  driverStats: {
    total: number // Total number of drivers in formula
    withValues: number // Drivers that have values
    missingValues: number // Drivers missing values
  } | null
}

/**
 * Hook to analyze formula calculation status for a cell
 *
 * Returns detailed status about formula drivers:
 * - formulaStatus: 'complete' (all drivers have values), 'partial' (some missing), 'none' (no values), null (no formula)
 * - driverStats: counts of total/withValues/missingValues drivers
 * - hasFormulaCalculation: backward compatible boolean (true if status is 'complete')
 * - periodInfo: year/month if applicable
 *
 * Prerequisites:
 * 1. Row has a driver budgeting method with a formula
 * 2. Column has period information (year/month)
 * 3. Column type allows formula calculation:
 *    - Budget: always allowed
 *    - Forecast: only if after forecastCursor
 *    - Actuals: never allowed
 */
export const useFormulaCalculation = ({
  data,
  statementColumn
}: UseFormulaCalculationParams): UseFormulaCalculationReturn => {
  const { driverData, budgetDriverMap, isLoading: isDriverDataLoading } = useDriverDataContext()
  const forecastCursor = useSelector(forecastCursorSelector)

  const analysisResult = useMemo(() => {
    // Basic checks: must have driver method and source formula
    if (!data?.budgetingMethod?.method || data.budgetingMethod.method !== 'driver' || !data.budgetingMethod.source) {
      return { status: null as FormulaStatus, stats: null }
    }

    // No column data at all - cannot determine formula status
    if (!statementColumn) {
      return { status: null as FormulaStatus, stats: null }
    }

    // Fiscal year column: has formula but no month (only show formula, no status)
    // But not for actuals columns
    if (!statementColumn.month || statementColumn.dataSource === 'groups') {
      if (statementColumn.dataType === StatementDataType.actuals) {
        return { status: null as FormulaStatus, stats: null }
      }
      return { status: 'fiscalYear' as FormulaStatus, stats: null }
    }

    // Monthly column: need year, month, and driver data
    if (!statementColumn?.year || isDriverDataLoading) {
      return { status: null as FormulaStatus, stats: null }
    }

    // Check if column type allows formula calculation
    const { dataType, year, month } = statementColumn

    // Actuals never allow formula calculation
    if (dataType === StatementDataType.actuals) {
      return { status: null as FormulaStatus, stats: null }
    }

    // Budget always allows formula calculation
    // Forecast allows formula calculation only if after forecastCursor
    if (dataType === StatementDataType.forecast) {
      if (
        !forecastCursor ||
        !(year > forecastCursor.year() || (year === forecastCursor.year() && month > forecastCursor.month() + 1))
      ) {
        return { status: null as FormulaStatus, stats: null }
      }
    }

    // Extract variable names from formula
    const variableNames = extractVariableNames(data.budgetingMethod.source)
    if (variableNames.length === 0) {
      return { status: null as FormulaStatus, stats: null }
    }

    // Check driver values and count statistics
    const dimensionId = data.dimensionId || null
    let driversWithValues = 0
    let driversMissingValues = 0

    variableNames.forEach(variableName => {
      const driver = findDriverForVariable(variableName, driverData, budgetDriverMap, dimensionId)
      if (!driver) {
        driversMissingValues += 1
        return
      }

      const driverValue = getDriverValueForPeriod(driver, year, month)
      if (driverValue !== null) {
        driversWithValues += 1
      } else {
        driversMissingValues += 1
      }
    })

    const totalDrivers = variableNames.length
    const stats = {
      total: totalDrivers,
      withValues: driversWithValues,
      missingValues: driversMissingValues
    }

    // Determine status based on statistics
    let status: FormulaStatus
    if (driversWithValues === totalDrivers) {
      status = 'complete'
    } else if (driversWithValues > 0) {
      status = 'partial'
    } else {
      status = 'none'
    }

    return { status, stats }
  }, [data, statementColumn, driverData, budgetDriverMap, isDriverDataLoading, forecastCursor])

  const periodInfo = useMemo(() => {
    // No period info for fiscal year columns or null status
    if (
      !analysisResult.status ||
      analysisResult.status === null ||
      analysisResult.status === 'fiscalYear' ||
      !statementColumn?.year ||
      !statementColumn?.month
    ) {
      return null
    }

    return {
      year: statementColumn.year,
      month: statementColumn.month
    }
  }, [analysisResult.status, statementColumn])

  // Derive hasFormulaCalculation for backward compatibility
  const hasFormulaCalculation = analysisResult.status === 'complete'

  return {
    hasFormulaCalculation,
    formulaStatus: analysisResult.status,
    driverStats: analysisResult.stats,
    periodInfo
  }
}
